/**
 * Comparing an NMI across modules that store it differently.
 *
 * ⚠️ TWO MODULES, TWO FORMATS, AND A DUPLICATE SITE EVERY TIME THEY MEET.
 *
 * Bill Validation stores the FULL NMI as printed on a bill — the ten-character AEMO identifier
 * plus its checksum digit. On prod, 5,383 of 5,411 sites are eleven characters. A tender
 * typically carries the ten-character identifier on its own.
 *
 * The tender's duplicate check compared the raw strings, so `2001515871` never matched BV's
 * `20015158717` and a second site row was created for the same physical meter. Measured on prod:
 * 421 NMIs have duplicate site rows, most of them spread across two or three client records
 * because the check was also scoped to one client.
 *
 * So comparison happens on the base, and only on the base. Never use it for display or storage —
 * the stored value keeps whatever the retailer printed.
 */

/** The AEMO identifier length; an eleventh character on a bill is the checksum. */
export const NMI_BASE_LENGTH = 10;

/** Upper-cased, stripped of anything that is not a letter or digit. */
export const normaliseNmi = (raw: string | null | undefined): string =>
  typeof raw === 'string' ? raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, '') : '';

/**
 * The ten-character identifier, with any checksum digit dropped.
 *
 * Shorter values are returned as they are rather than padded — a partial NMI is the caller's
 * problem to validate, and inventing characters here would make two different meters compare
 * equal.
 */
export const nmiBase = (raw: string | null | undefined): string =>
  normaliseNmi(raw).slice(0, NMI_BASE_LENGTH);

/**
 * Do these two NMIs identify the same meter?
 *
 * Empty input never matches anything — an unknown NMI must not collide with another unknown one,
 * or every undated site would be treated as the same place.
 */
export const isSameNmi = (a: string | null | undefined, b: string | null | undefined): boolean => {
  const left = nmiBase(a);
  const right = nmiBase(b);
  return left.length > 0 && left === right;
};
